import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { supabase } from '@/lib/supabase';
import {
  mapOrderV2Rows,
  mapOrderLinesToItems,
  ORDER_V2_FULL_SELECT,
  ORDER_LINES_ITEM_SELECT,
  type OrderFlat,
  type OrderItemFlat,
} from '@/lib/operations/orderFromOrdersV2';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { useAuth } from '@smarty/shared';
import { useRealtimeSubscription } from '@smarty/shared';
import { cacheOrders, cacheOrderItems, getCachedOrdersByStatus, getCachedOrderItems } from '@/lib/db';
import { logInfo, logError } from '@/lib/logger';
import { useListaCacheada } from '@/hooks/useDatosCacheados';
import { queryClient } from '@/lib/queryClient';
import { 
  Page, 
  Card, 
  Banner, 
  Button,
  Text, 
  Box, 
  InlineStack, 
  BlockStack,
  Badge,
  EmptyState,
  Select,
  Tabs,
  Checkbox,
  InlineGrid,
  SkeletonBodyText,
  SkeletonDisplayText,
} from '@/lib/polaris';
import { OrderIcon, StoreIcon, GlobeIcon, ChatIcon } from '@/lib/polaris-icons';
import { Icon } from '@/lib/polaris';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { StatsSnapshotBar } from '@/components/admin/StatsSnapshotBar';

// `type` (no `interface`): un type-alias de objeto es asignable a tipos con index
// signature como `CachedRecord` (lo exigen cacheOrders/cacheOrderItems, tanda #122).
type OrderItem = {
  id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  // Los modifiers del jsonb existen en DOS formatos históricos: string legacy
  // ("Sin ají") u objeto {id, name, price_adjustment}. El render debe distinguirlos.
  modifiers: Array<string | { id: string; name: string; price_adjustment: number }>;
  notes?: string | null;
};

// Item tal como se persiste en el caché offline (store `orderItems`, índice by-order).
type CachedOrderItem = OrderItem & { order_id: string };

// Orden aplanada tal como la usa el KDS (sin items: van en su propio store).
type OrderBase = {
  id: string;
  order_number: number | null;
  table_number: string | null;
  waiter_name: string | null;
  status: string;
  total: number;
  created_at: string;
  payment_method?: string | null;
  notes?: string | null;
  order_type?: 'pos' | 'ecommerce' | 'qr_table' | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  delivery_method?: 'pickup' | 'delivery' | null;
  delivery_address?: string | null;
};

type Order = OrderBase & { items: OrderItem[] };

// Orden tal como se persiste en el caché offline: getCachedOrdersByStatus filtra por
// tenant_id, así que hay que guardarlo sí o sí (sin él, el caché quedaría invisible).
type CachedKdsOrder = OrderBase & { tenant_id: string };

// Normaliza el jsonb crudo de modifiers al union real (string legacy u objeto con name).
function parseKdsModifiers(raw: unknown): OrderItem['modifiers'] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (m): m is string | { id: string; name: string; price_adjustment: number } =>
      typeof m === 'string' || (m !== null && typeof m === 'object' && typeof m.name === 'string'),
  );
}

function toCachedKdsOrder(o: OrderFlat, tenantId: string): CachedKdsOrder {
  return {
    id: o.id,
    tenant_id: o.tenant_id ?? tenantId,
    order_number: o.order_number,
    table_number: o.table_number,
    waiter_name: o.waiter_name,
    status: o.status,
    total: o.total ?? 0,
    // created_at no debería venir null; fallback a "ahora" para no romper los timers.
    created_at: o.created_at ?? new Date().toISOString(),
    payment_method: o.payment_method,
    notes: o.notes,
    order_type:
      o.order_type === 'pos' || o.order_type === 'ecommerce' || o.order_type === 'qr_table'
        ? o.order_type
        : null,
    customer_name: o.customer_name,
    customer_phone: o.customer_phone,
    delivery_method:
      o.delivery_method === 'pickup' || o.delivery_method === 'delivery' ? o.delivery_method : null,
    delivery_address: o.delivery_address,
  };
}

function toCachedOrderItem(it: OrderItemFlat, orderId: string, idx: number): CachedOrderItem {
  return {
    // order_lines.id no debería faltar; fallback determinista para la key/checkbox del KDS.
    id: it.id ?? `${orderId}-${idx}`,
    order_id: orderId,
    product_id: it.product_id ?? '',
    product_name: it.product_name ?? '',
    quantity: it.quantity,
    unit_price: it.unit_price ?? 0,
    modifiers: parseKdsModifiers(it.modifiers),
    notes: it.notes,
  };
}

type TimeFilter = 'all' | 'new' | 'in-progress' | 'delayed' | 'ready';

export default function KDSPage() {
  const navigate = useNavigate();
  const storeNavigate = useStoreNavigate();
  const { tenantId, loading: authLoading } = useAuth();
  const toast = useAdminToast();
  const [isRealtime, setIsRealtime] = useState(true);
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [checkedItems, setCheckedItems] = useState<Record<string, Set<string>>>({});

  const claveKds = useMemo(() => ['kds-ordenes', tenantId], [tenantId]);

  /*
   * ESTA PANTALLA TIENE DOS CACHÉS, Y CADA UNA RESUELVE UN PROBLEMA DISTINTO.
   *
   *  · IndexedDB (`cacheOrders` / `getCachedOrdersByStatus`) es la caché OFFLINE. La cocina no
   *    puede quedarse sin comandas porque se cayó el wifi, así que las órdenes sobreviven al
   *    cierre del navegador. Eso no lo hace la caché del panel, que vive en memoria.
   *
   *  · La caché del panel es la de NAVEGACIÓN: volver a KDS desde otra pantalla pinta al instante
   *    en vez de repetir la consulta.
   *
   * No se pisan y ninguna reemplaza a la otra. Lo único que hay que coser es el arranque en frío:
   * ver más abajo.
   */
  const { datos: orders, cargando: loading, error: errorCarga, recargar } = useListaCacheada<Order>({
    clave: claveKds,
    activo: !authLoading && !!tenantId,
    traer: async () => {
      if (!navigator.onLine) {
        // Sin red, la verdad es lo último que guardamos en disco.
        logInfo('Modo offline - usando solo caché');
        const cachedOrders = await getCachedOrdersByStatus<CachedKdsOrder>(['pending', 'preparing', 'ready']);
        return Promise.all(
          cachedOrders.map(async (order) => ({
            ...order,
            items: (await getCachedOrderItems<CachedOrderItem>(order.id)) || [],
          })),
        );
      }

      if (!tenantId) {
        throw new Error('No se encontró el tenant. Por favor inicia sesión nuevamente.');
      }

      // Dominio único orders_v2: status (pending/preparing/ready) vive en source_metadata.legacy_status,
      // así que traemos los no-terminales y filtramos tras aplanar a la forma legacy.
      const { data: rawOrders, error: ordersError } = await supabase
        .from('orders_v2')
        .select(ORDER_V2_FULL_SELECT)
        .eq('tenant_id', tenantId)
        .in('lifecycle_stage', ['quotation', 'sale', 'fulfilled'])
        .order('created_at', { ascending: true });

      if (ordersError) throw ordersError;

      // Se cachea la MISMA forma que se lee (CachedKdsOrder): shape alineado escritura/lectura.
      const ordersData = mapOrderV2Rows(rawOrders)
        .filter((o) => ['pending', 'preparing', 'ready'].includes(o.status))
        .map((o) => toCachedKdsOrder(o, tenantId));

      if (ordersData.length > 0) {
        await cacheOrders(ordersData);
      }

      const allItems: CachedOrderItem[] = [];
      const ordersWithItems: Order[] = await Promise.all(
        ordersData.map(async (order) => {
          const { data: lines } = await supabase
            .from('order_lines')
            .select(ORDER_LINES_ITEM_SELECT)
            .eq('order_id', order.id);

          const items = mapOrderLinesToItems(lines).map((it, idx) => toCachedOrderItem(it, order.id, idx));
          allItems.push(...items);

          return { ...order, items };
        }),
      );

      if (allItems.length > 0) {
        await cacheOrderItems(allItems);
      }

      logInfo(`Órdenes KDS actualizadas: ${ordersWithItems.length}`);
      return ordersWithItems;
    },
  });

  const loadOrders = useCallback(() => { void recargar(); }, [recargar]);

  /*
   * El arranque en frío: siembra desde el disco mientras la red contesta.
   *
   * En una recarga de página la caché del panel está vacía —vive en memoria—, así que sin esto la
   * cocina vería el spinner aunque tenga las comandas guardadas en disco desde hace un segundo. Y
   * una pantalla de cocina se recarga: está colgada de una pared, encendida todo el día.
   *
   * 🪤 Sólo siembra si NO hay nada en la caché todavía. Si el `traer` ya contestó, escribir acá
   * pisaría datos frescos con datos de disco — la carrera al revés, y en una pantalla que decide
   * qué se está cocinando.
   */
  useEffect(() => {
    if (authLoading || !tenantId) return;
    let cancelado = false;
    void (async () => {
      if (queryClient.getQueryData(claveKds) !== undefined) return;
      const cachedOrders = await getCachedOrdersByStatus<CachedKdsOrder>(['pending', 'preparing', 'ready']);
      if (cancelado || cachedOrders.length === 0) return;
      if (queryClient.getQueryData(claveKds) !== undefined) return;
      const conItems: Order[] = await Promise.all(
        cachedOrders.map(async (order) => ({
          ...order,
          items: (await getCachedOrderItems<CachedOrderItem>(order.id)) || [],
        })),
      );
      if (cancelado || queryClient.getQueryData(claveKds) !== undefined) return;
      queryClient.setQueryData<Order[]>(claveKds, conItems);
      logInfo(`Órdenes KDS cargadas desde caché: ${conItems.length}`);
    })();
    return () => { cancelado = true; };
  }, [claveKds, authLoading, tenantId]);

  const error = errorCarga?.message ?? null;

  // Sonido de notificación
  const playNotificationSound = useCallback(() => {
    try {
      // Crear un sonido "ding" usando Web Audio API
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Safari browser compat
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // Frecuencia del "ding"
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (err) {
      logError('No se pudo reproducir el sonido de notificación', err);
    }
  }, []);

  // Verificar si el usuario ha interactuado con la página (para sonidos)
  const userHasInteractedRef = useRef(false);
  
  useEffect(() => {
    const markInteraction = () => {
      userHasInteractedRef.current = true;
      document.removeEventListener('click', markInteraction);
      document.removeEventListener('keydown', markInteraction);
    };
    document.addEventListener('click', markInteraction);
    document.addEventListener('keydown', markInteraction);
    
    return () => {
      document.removeEventListener('click', markInteraction);
      document.removeEventListener('keydown', markInteraction);
    };
  }, []);

  // Suscripción a Realtime usando hook con cleanup automático
  useRealtimeSubscription({
    channel: 'kds-orders',
    // Dominio único: `orders` es una VISTA → realtime (postgres_changes) debe escuchar la tabla
    // real orders_v2. La lectura/escritura de KDS sigue por la vista `orders` (mismo shape).
    table: 'orders_v2',
    filter: tenantId ? `tenant_id=eq.${tenantId}` : undefined,
    enabled: !authLoading && !!tenantId,
    onInsert: (payload) => {
      const orderNum = payload.new.order_number 
        ? `#${payload.new.order_number.toString().padStart(4, '0')}`
        : 'Nueva';
      toast.success('¡Nueva orden recibida!', `Orden ${orderNum}`);

      // Reproducir sonido solo si el usuario ha interactuado
      if (userHasInteractedRef.current) {
        playNotificationSound();
      }

      loadOrders();
    },
    onUpdate: () => {
      loadOrders();
    },
    onEvent: (event, payload) => {
      if (event === 'SUBSCRIBED') {
        setIsRealtime(true);
      } else if (event === 'CHANNEL_ERROR') {
        setIsRealtime(false);
      }
    },
  });

  // Filtrar órdenes por tiempo
  const getFilteredOrders = useMemo(() => {
    let filtered = orders;

    // Filtrar por canal
    if (channelFilter !== 'all') {
      filtered = filtered.filter((order) => {
        if (channelFilter === 'qr_table') return order.order_type === 'qr_table';
        if (channelFilter === 'ecommerce') return order.order_type === 'ecommerce';
        if (channelFilter === 'pos') return order.order_type === 'pos' || (!order.order_type && !order.order_type);
        // order_type==='ecommerce' ya retorno arriba: esa mitad era codigo muerto.
      const isWeb = order.payment_method === 'public';
        if (channelFilter === 'web') return isWeb;
        if (channelFilter === 'whatsapp') return order.payment_method === 'whatsapp';
        return true;
      });
    }

    // Filtrar por tiempo
    if (timeFilter !== 'all') {
      filtered = filtered.filter((order) => {
        const elapsed = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
        
        if (order.status === 'ready') return timeFilter === 'ready';
        if (timeFilter === 'new') return elapsed < 5;
        if (timeFilter === 'in-progress') return elapsed >= 5 && elapsed < 15;
        if (timeFilter === 'delayed') return elapsed >= 15;
        return true;
      });
    }

    return filtered;
  }, [orders, channelFilter, timeFilter]);

  // Tabs para filtros de tiempo
  const timeTabs = [
    { id: 'all', content: 'Todas' },
    { id: 'new', content: 'Nuevas (<5m)' },
    { id: 'in-progress', content: 'En Proceso (5-15m)' },
    { id: 'delayed', content: 'Retrasadas (>15m)' },
    { id: 'ready', content: 'Listas' },
  ];

  const selectedTabIndex = timeTabs.findIndex(tab => tab.id === timeFilter);

  const kdsStats = useMemo(() => {
    const total = orders.length;
    const pending = orders.filter((o) => o.status === 'pending').length;
    const preparing = orders.filter((o) => o.status === 'preparing').length;
    const ready = orders.filter((o) => o.status === 'ready').length;
    return { total, pending, preparing, ready };
  }, [orders]);

  const channelOptions = [
    { label: 'Todos', value: 'all' },
    { label: 'POS', value: 'pos' },
    { label: 'Autopedido', value: 'qr_table' },
    { label: 'Ecommerce', value: 'ecommerce' },
    { label: 'Web', value: 'web' },
    { label: 'WhatsApp', value: 'whatsapp' },
  ];

  const handleMarkAllReady = async (orderId: string) => {
    try {
      // Dominio único: el status legacy (ready) se persiste en source_metadata vía RPC.
      const { error } = await supabase.rpc('fn_set_order_legacy_status', {
        p_order_id: orderId,
        p_status: 'ready',
      });

      if (error) throw error;

      toast.success('Orden marcada como lista');
      loadOrders();
      // Limpiar checkboxes de esta orden
      setCheckedItems((prev) => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
    } catch (err: unknown) {
      toast.error('Error al actualizar orden', (err as Error)?.message);
    }
  };

  const toggleItemCheck = (orderId: string, itemId: string) => {
    setCheckedItems((prev) => {
      const next = { ...prev };
      if (!next[orderId]) {
        next[orderId] = new Set();
      }
      const orderChecked = new Set(next[orderId]);
      if (orderChecked.has(itemId)) {
        orderChecked.delete(itemId);
      } else {
        orderChecked.add(itemId);
      }
      next[orderId] = orderChecked;
      return next;
    });
  };

  // Componente de Tarjeta de Comanda
  const OrderCard = ({ order }: { order: Order }) => {
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [isBlinking, setIsBlinking] = useState(false);

    useEffect(() => {
      const calculateElapsedTime = () => {
        const now = new Date();
        const createdAt = new Date(order.created_at);
        const seconds = Math.floor((now.getTime() - createdAt.getTime()) / 1000);
        setElapsedSeconds(seconds);
      };

      calculateElapsedTime();
      const interval = setInterval(calculateElapsedTime, 1000); // Actualizar cada segundo
      return () => clearInterval(interval);
    }, [order.created_at]);

    useEffect(() => {
      const minutes = Math.floor(elapsedSeconds / 60);
      if (minutes >= 15 && order.status !== 'ready') {
        setIsBlinking(true);
      } else {
        setIsBlinking(false);
      }
    }, [elapsedSeconds, order.status]);

    const elapsedMinutes = Math.floor(elapsedSeconds / 60);

    const getBorderColor = () => {
      if (order.status === 'ready') return '#005bd3'; // Azul highlight
      if (elapsedMinutes < 5) return '#10b981'; // Verde emerald-500
      if (elapsedMinutes < 15) return '#facc15'; // Amarillo yellow-400
      return '#e11d48'; // Rojo rose-600
    };

    const getCardBackground = () => {
      if (elapsedMinutes >= 15 && order.status !== 'ready') return '#fff1f2'; // Rojo suave rose-50
      return undefined; // Usar fondo por defecto de Polaris
    };

    const getChannelInfo = () => {
      if (order.order_type === 'qr_table') {
        return { label: 'AUTOPEDIDO', icon: OrderIcon, tone: 'attention' as const };
      }
      if (order.order_type === 'ecommerce') {
        return { label: 'Ecommerce', icon: GlobeIcon, tone: 'info' as const };
      }
      // order_type==='ecommerce' ya retorno arriba: esa mitad era codigo muerto.
      const isWeb = order.payment_method === 'public';
      if (isWeb) {
        return { label: 'Web', icon: GlobeIcon, tone: 'info' as const };
      }
      if (order.payment_method === 'whatsapp') {
        return { label: 'WhatsApp', icon: ChatIcon, tone: 'success' as const };
      }
      return { label: 'POS', icon: StoreIcon, tone: 'info' as const };
    };

    const channelInfo = getChannelInfo();
    const orderCheckedItems = checkedItems[order.id] || new Set();
    const allChecked = order.items.length > 0 && order.items.every((item) => orderCheckedItems.has(item.id));

    const formatTime = (totalSeconds: number) => {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const borderColor = getBorderColor();
    const cardBackground = getCardBackground();

    return (
      <div
        style={{
          position: 'relative',
          backgroundColor: cardBackground || 'var(--p-color-bg-surface)',
          borderRadius: 'var(--p-border-radius-300)',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'var(--p-color-border-subdued)',
          overflow: 'hidden',
        }}
      >
        {/* Borde superior de color */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            backgroundColor: borderColor,
            zIndex: 1,
            borderTopLeftRadius: 'var(--p-border-radius-300)',
            borderTopRightRadius: 'var(--p-border-radius-300)',
          }}
        />
        <Card>
          {/* Header */}
          <Box padding="400" paddingBlockEnd="300">
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="100">
                <InlineStack gap="200" align="start">
                  <Text as="h3" variant="headingSm" fontWeight="semibold">
                    #{order.order_number?.toString().padStart(4, '0') || 'N/A'}
                  </Text>
                  <InlineStack gap="100" blockAlign="center">
                    <Icon source={channelInfo.icon} />
                    <Badge tone={channelInfo.tone}>
                      {channelInfo.label}
                    </Badge>
                  </InlineStack>
                </InlineStack>
                {order.table_number && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    Mesa: {order.table_number}
                  </Text>
                )}
              </BlockStack>
              <Box>
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 'var(--p-font-size-400)',
                    fontWeight: 'bold',
                    color: elapsedMinutes >= 15 ? 'var(--p-color-text-critical)' : 'var(--p-color-text)',
                    animation: isBlinking ? 'blink 1s infinite' : undefined,
                  }}
                >
                  {formatTime(elapsedSeconds)}
                </div>
              </Box>
            </InlineStack>
          </Box>

          {/* Cuerpo - Items */}
          <Box paddingInlineStart="400" paddingInlineEnd="400" paddingBlockEnd="400">
            <BlockStack gap="100">
              {order.items.map((item) => {
                const isChecked = orderCheckedItems.has(item.id);
                return (
                  <Box key={item.id}>
                    <InlineStack gap="200" align="start">
                      <Checkbox
                        checked={isChecked}
                        onChange={() => toggleItemCheck(order.id, item.id)}
                        label=""
                      />
                      <div style={{ minWidth: 0, flexGrow: 1 }}>
                        <Text as="p" variant="bodyMd" fontWeight="medium">
                          {item.quantity}x {item.product_name}
                        </Text>
                        {item.modifiers && item.modifiers.length > 0 && (
                          <BlockStack gap="050">
                            {item.modifiers.map((modifier, idx: number) => (
                              <Text key={idx} as="span" variant="bodySm" tone="subdued">
                                {/* Dos formatos reales: string legacy u objeto — nunca renderizar el objeto directo */}
                                • {typeof modifier === 'string' ? modifier : modifier.name}
                              </Text>
                            ))}
                          </BlockStack>
                        )}
                        {item.notes && (
                          <Text as="p" variant="bodySm" tone="subdued">
                            Nota: {item.notes}
                          </Text>
                        )}
                      </div>
                    </InlineStack>
                  </Box>
                );
              })}
            </BlockStack>
          </Box>

          {/* Footer - Botón */}
          {order.status !== 'ready' && (
            <Box padding="400" paddingBlockStart="300">
              <Button
                fullWidth
                variant={elapsedMinutes >= 15 ? 'primary' : 'secondary'}
                onClick={() => handleMarkAllReady(order.id)}
              >
                Marcar Listo
              </Button>
            </Box>
          )}

          {order.status === 'ready' && (
            <Box padding="400" paddingBlockStart="300">
              <Badge tone="info" size="large">
                Lista para entregar
              </Badge>
            </Box>
          )}
        </Card>
      </div>
    );
  };

  const showKdsShellLoading = loading || authLoading;

  return (
    <Page
      title={
        <PageBreadcrumbTitle
          icon={OrderIcon}
          title="Monitor de cocina"
          onBack={() => storeNavigate(ROUTES.ORDERS)}
          backAccessibilityLabel="Volver a Pedidos"
        />
      }
    >
        {showKdsShellLoading ? (
          <Box padding="400" minHeight="50vh">
            <BlockStack gap="400">
              <SkeletonDisplayText size="large" />
              <SkeletonBodyText lines={16} />
            </BlockStack>
          </Box>
        ) : (
        <>
        <Box paddingBlockEnd="400">
          <StatsSnapshotBar
            items={[
              { label: 'Total', value: kdsStats.total },
              { label: 'Pendientes', value: kdsStats.pending, tone: 'caution' },
              { label: 'En preparación', value: kdsStats.preparing, tone: 'info' },
              { label: 'Listas', value: kdsStats.ready, tone: 'success' },
            ]}
          />
        </Box>

        {error && (
          <Box paddingBlockEnd="400">
            <Banner tone="critical" title="Error al cargar órdenes">
              <p>{error}</p>
            </Banner>
          </Box>
        )}

        {!isRealtime && (
          <Box paddingBlockEnd="400">
            <Banner tone="warning" title="Modo offline">
              <p>No hay conexión en tiempo real. Los datos pueden estar desactualizados.</p>
            </Banner>
          </Box>
        )}

        {/* Filtros */}
        <Box paddingBlockEnd="400">
          <Card>
            <BlockStack gap="400">
              <Tabs
                tabs={timeTabs}
                selected={selectedTabIndex}
                onSelect={(index) => setTimeFilter(timeTabs[index].id as TimeFilter)}
              >
                <Box paddingBlockStart="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <Box />
                    <Box minWidth="200px">
                      <Select
                        label="Canal"
                        labelHidden
                        options={channelOptions}
                        value={channelFilter}
                        onChange={setChannelFilter}
                      />
                    </Box>
                  </InlineStack>
                </Box>
              </Tabs>
            </BlockStack>
          </Card>
        </Box>

        {/* Grid de Comandas */}
        {getFilteredOrders.length === 0 ? (
          <Card>
            <EmptyState
              heading="No hay órdenes"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>No hay órdenes que coincidan con los filtros seleccionados.</p>
            </EmptyState>
          </Card>
        ) : (
          <Box>
            <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 5 }} gap="400">
              {getFilteredOrders.map((order) => (
                <Box key={order.id}>
                  <OrderCard order={order} />
                </Box>
              ))}
            </InlineGrid>
          </Box>
        )}
        </>
        )}
    </Page>
  );
}
